import GuiContext from "config/context/guiContext";
import { useCallback, useContext, useState } from "react";
import { posaljiSaRuckom } from "./fileHandles";

/**
 * Pita izvor šta u njemu postoji — tabele, kolekcije, prefiksi ključeva.
 *
 * Zašto uopšte: bez ovoga korisnik ime tabele kuca naslepo. U MySQL-u je to
 * neprijatno, u Redisu nemoguće — Redis nema katalog, pa nema načina da se
 * sazna koji prefiksi ključeva postoje osim da se baza uzorkuje.
 *
 * Nije ničije posebno. Plugin koji to ume javlja `targets: true` u katalogu, a
 * polje koje se time popunjava nosi `browse` u svojoj šemi. Forma zna samo
 * „ako i jedno i drugo stoji, nacrtaj dugme" — isti obrazac kao `inspect`.
 *
 * Za razliku od `inspect`, ovome TREBAJU kredencijali, pa se poziva na klik, a
 * ne dok korisnik kuca.
 */
export const useBrowseTargets = () => {
    const [targets, setTargets] = useState(null);
    const [note, setNote] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const { selected: { selectedServer }, userData, URL } = useContext(GuiContext);

    /**
     * @param {string} slug
     * @param {object} config
     * @param {File} [file]  izvor koji nije konekcija nego FAJL — dump. Tabele
     *   se čitaju IZ njega, pa ga server mora imati; šalje se ručka.
     * @param {string} [kind]  ŠTA se nabraja — vrednost `browse` polja koje je
     *   pritisnuto. Jedan izvor ume dva nivoa: `entries` su fajlovi u arhivi,
     *   `targets` tabele u njima. Ruta je jedna, pa bez ovoga ne bi mogla da
     *   posluži oba.
     */
    const browse = useCallback(async (slug, config, file, kind) => {
        setLoading(true);
        setError(null);
        try {
            const polja = {
                server_url: selectedServer || "",
                guiname: userData?.guiname || "",
                account_id: userData?.id || "",
                config,
                ...(kind ? { browse: kind } : {}),
            };

            // Fajl ide na server JEDNOM; ovaj korak i svaki naredni „Browse"
            // nose samo ručku. Vidi `fileHandles.js`.
            const data = file
                ? await posaljiSaRuckom({
                    URL, server: selectedServer, slug, file, ruta: "/targets", polja,
                })
                : await (await fetch(`${URL}/api/imports/${slug}/targets`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(polja),
                })).json();

            if (!data?.status) {
                // Poziv je korisnik izričito zatražio klikom, pa se greška
                // pokazuje. Ovde je ćutanje najgori mogući ishod: dugme koje
                // ništa ne uradi izgleda kao pokvarena aplikacija.
                setError(data?.message || "Could not read the source");
                setTargets(null);
                return null;
            }

            setTargets(data.targets || []);
            setNote(data.note || null);
            return data.targets || [];
        } catch (err) {
            setError(err.message || "Could not reach the server");
            setTargets(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, [URL, selectedServer, userData]);

    const reset = useCallback(() => {
        setTargets(null);
        setNote(null);
        setError(null);
    }, []);

    return { browse, reset, targets, note, error, loading };
};
